// StaffTransactions.jsx - MERGED VIEW (Plan + Commission Side by Side)

import React, { useEffect, useState } from "react";
import {
  Table, Tag, Input, Select, DatePicker, Button, message, Tooltip,
  Drawer, Divider, Dropdown, Space,
} from "antd";
import {
  SearchOutlined, TransactionOutlined, EyeOutlined, ReloadOutlined,
  FileExcelOutlined, FilePdfOutlined, DownOutlined, RiseOutlined,
  FallOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
  DollarOutlined, CalendarOutlined, IdcardOutlined, CreditCardOutlined,
  FileTextOutlined, WalletOutlined, UserOutlined, GiftOutlined,
  ShoppingOutlined, TrophyOutlined,PhoneOutlined,
} from "@ant-design/icons";
import { StaffTransactionsFilteredGet } from "../../../../Api/CoreApi";
import { exportToCSV } from "../../../Admin_Panel/Utils/exportCSV";
import { exportToPDF } from "../../../Admin_Panel/Utils/exportPDF";
import {
  formatDate, formatDateTime, generateRefNumber, capitalize,
} from "../../../Admin_Panel/Utils/formatters";
import "./StaffTransactions.css";

const { Option } = Select;
const { RangePicker } = DatePicker;

function StaffTransactions() {
  const [mergedTransactions, setMergedTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [referredUsersCount, setReferredUsersCount] = useState(0);

  // ✅ Separate totals
  const [totals, setTotals] = useState({
    totalPlanPurchase: 0,   // Users ne kitna kharcha kiya
    totalCommission: 0,     // Staff ko kitna mila
    totalDebit: 0,          // Withdrawals
  });

  // ============================================
  // ✅ FETCH DATA & MERGE PLAN + COMMISSION
  // ============================================
  const fetchData = async () => {
    try {
      setLoading(true);

      const response = await StaffTransactionsFilteredGet();
      console.log("📥 Staff Filtered Response:", response);

      const userTxRaw = response.user_transactions || [];
      const staffTxRaw = response.staff_transactions || [];
      const count = response.referred_users_count || 0;

      setReferredUsersCount(count);

      // ============================================
      // ✅ MERGE LOGIC - Plan Purchase + Commission
      // ============================================
      // Ek user plan purchase karta hai to:
      // - user_transactions me plan amount (₹99)
      // - staff_transactions me commission (₹14.85)
      // Dono ko match karke ek row banayenge
      
      const merged = [];
      const usedStaffTxIds = new Set();

      // Step 1: User transactions ko process karo
      userTxRaw.forEach(userTx => {
        // Ye check karo ki ye plan purchase hai ya kuch aur
        const isPlanPurchase = userTx.type === 'credit' && 
                              userTx.plan_id && 
                              userTx.plan_name;

        // Find matching staff commission for same plan/user
        let matchingStaffTx = null;
        
        if (isPlanPurchase) {
          matchingStaffTx = staffTxRaw.find(staffTx => {
            if (usedStaffTxIds.has(staffTx.id)) return false;
            if (staffTx.type !== 'credit') return false;
            
            // Match by plan_id + user_id
            const samePlan = String(staffTx.plan_id || '') === String(userTx.plan_id || '');
            const sameUser = String(staffTx.user_id || '') === String(userTx.user_id || userTx.user || '');
            
            // Match by ref_id/description
            const sameRefId = staffTx.ref_id && userTx.ref_id && 
                             String(staffTx.ref_id).includes(String(userTx.id));
            
            // Match by close timestamp (within 5 minutes)
            let sameTime = false;
            if (staffTx.created_date && userTx.created_date) {
              const diff = Math.abs(
                new Date(staffTx.created_date) - new Date(userTx.created_date)
              );
              sameTime = diff < 5 * 60 * 1000; // 5 minutes
            }
            
            return (samePlan && (sameUser || sameTime)) || sameRefId;
          });
          
          if (matchingStaffTx) {
            usedStaffTxIds.add(matchingStaffTx.id);
          }
        }

        // Create merged row
        merged.push({
          id: userTx.id,
          _uniqueId: `merged-${userTx.id}`,
          _rowType: isPlanPurchase ? 'plan_purchase' : 'user_txn',
          
          // User transaction data
          userTx: userTx,
          
          // Matched commission (if any)
          commissionTx: matchingStaffTx,
          
          // Display fields
          date: userTx.created_date || userTx.date,
          user_name: userTx.user_name || userTx.username || 'N/A',
          plan_name: userTx.plan_name || null,
          plan_amount: parseFloat(userTx.amount || 0),
          commission_amount: matchingStaffTx ? parseFloat(matchingStaffTx.amount || 0) : 0,
          type: userTx.type,
          status: userTx.status,
          UTI: userTx.UTI,
          contact: userTx.contact,
          upi_id: userTx.upi_id,
        });
      });

      // Step 2: Staff transactions jo user ke saath merge nahi hue (standalone commission/withdrawal)
      staffTxRaw.forEach(staffTx => {
        if (usedStaffTxIds.has(staffTx.id)) return; // Already merged

        merged.push({
          id: staffTx.id,
          _uniqueId: `staff-${staffTx.id}`,
          _rowType: staffTx.type === 'credit' ? 'standalone_commission' : 'withdrawal',
          
          userTx: null,
          commissionTx: staffTx,
          
          date: staffTx.created_date || staffTx.date,
          user_name: staffTx.user_name || staffTx.staff_username || 'N/A',
          plan_name: staffTx.plan_name || null,
          plan_amount: 0,
          commission_amount: parseFloat(staffTx.amount || 0),
          type: staffTx.type,
          status: staffTx.status,
          UTI: staffTx.UTI,
          contact: staffTx.contact,
          upi_id: staffTx.upi_id,
        });
      });

      // Sort by date - latest first
      merged.sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateB - dateA;
      });

      // ✅ Calculate totals
      const totalPlanPurchase = merged.reduce((sum, r) => sum + r.plan_amount, 0);
      const totalCommission = merged.reduce((sum, r) => sum + (r.type === 'credit' ? r.commission_amount : 0), 0);
      const totalDebit = merged
        .filter(r => r.type === 'debit' || r.type === 'withdrawal')
        .reduce((sum, r) => sum + r.commission_amount, 0);

      console.log("=" .repeat(60));
      console.log("📊 MERGED TRANSACTION COUNTS:");
      console.log(`   Raw User: ${userTxRaw.length}`);
      console.log(`   Raw Staff: ${staffTxRaw.length}`);
      console.log(`   Merged Rows: ${merged.length}`);
      console.log(`   Plan Purchase Total: ₹${totalPlanPurchase}`);
      console.log(`   My Commission Total: ₹${totalCommission}`);
      console.log("=" .repeat(60));

      setMergedTransactions(merged);
      setTotals({
        totalPlanPurchase,
        totalCommission,
        totalDebit,
      });

      if (merged.length === 0) {
        message.info(`No transactions yet. You have ${count} referred users.`);
      } else {
        message.success(`Loaded ${merged.length} transactions`);
      }

    } catch (error) {
      console.error("❌ Fetch Error:", error);
      message.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ============================================
  // FILTER
  // ============================================
  const getFiltered = () =>
    mergedTransactions.filter((tx) => {
      const name = (tx.user_name || '').toLowerCase();
      const matchSearch = !searchText ||
        name.includes(searchText.toLowerCase()) ||
        generateRefNumber("transaction", tx.id).toLowerCase().includes(searchText.toLowerCase()) ||
        String(tx.plan_amount).includes(searchText) ||
        String(tx.commission_amount).includes(searchText) ||
        (tx.UTI || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (tx.plan_name || '').toLowerCase().includes(searchText.toLowerCase());
      
      const matchStatus = statusFilter === "all" || tx.status === statusFilter;
      
      let matchDate = true;
      if (dateRange && dateRange.length === 2) {
        const d = new Date(tx.date);
        matchDate = d >= dateRange[0].toDate() && d <= dateRange[1].toDate();
      }
      
      return matchSearch && matchStatus && matchDate;
    });

  const filtered = getFiltered();

  // ✅ Stats
  const stats = {
    total: filtered.length,
    planPurchase: filtered.reduce((s, t) => s + t.plan_amount, 0),
    commission: filtered.filter(t => t.type === 'credit').reduce((s, t) => s + t.commission_amount, 0),
    debit: filtered.filter(t => t.type === 'debit' || t.type === 'withdrawal').reduce((s, t) => s + t.commission_amount, 0),
    success: filtered.filter(t => t.status === "success" || t.status === "approved" || t.status === "completed").length,
    pending: filtered.filter(t => t.status === "pending").length,
    failed: filtered.filter(t => t.status === "failed" || t.status === "rejected").length,
  };

  // ✅ Export columns
  const exportColumns = [
    { key: "sno", label: "S.No." },
    { key: "ref", label: "Ref", format: (r) => generateRefNumber("transaction", r.id) },
    { key: "user", label: "User", format: (r) => r.user_name || 'N/A' },
    { key: "plan", label: "Plan", format: (r) => r.plan_name || '-' },
    { key: "plan_amount", label: "Plan Amount (₹)", format: (r) => r.plan_amount.toFixed(2) },
    { key: "commission", label: "My Commission (₹)", format: (r) => r.commission_amount.toFixed(2) },
    { key: "status", label: "Status", format: (r) => capitalize(r.status) || "N/A" },
    { key: "date", label: "Date", format: (r) => formatDateTime(r.date) },
  ];

  const csvMenu = {
    items: [
      { key: "1", label: `Filtered (${filtered.length})`, onClick: () => exportToCSV(filtered, exportColumns, "staff_transactions") },
      { key: "2", label: `All (${mergedTransactions.length})`, onClick: () => exportToCSV(mergedTransactions, exportColumns, "staff_transactions") },
    ],
  };

  const pdfMenu = {
    items: [
      { key: "1", label: `Filtered (${filtered.length})`, onClick: () => exportToPDF(filtered, exportColumns, { title: "Staff Transaction History", filename: "staff_transactions" }) },
      { key: "2", label: `All (${mergedTransactions.length})`, onClick: () => exportToPDF(mergedTransactions, exportColumns, { title: "Staff Transaction History", filename: "staff_transactions" }) },
    ],
  };

  const clearFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setDateRange(null);
  };

  // ============================================
  // ✅ COLUMNS - Plan + Commission Side by Side
  // ============================================
  const columns = [
    {
      title: "#", key: "sno", width: 45, fixed: "left",
      render: (_, __, i) => <span className="stx-sno">{i + 1}</span>,
    },
    {
      title: "Ref", key: "ref", width: 100,
      render: (_, r) => <span className="stx-ref">{generateRefNumber("transaction", r.id)}</span>,
    },
    {
      title: "User", key: "user", width: 140,
      render: (_, r) => {
        return (
          <div>
            <div style={{ fontWeight: 600, fontSize: 12 }}>{r.user_name}</div>
            {r.contact && (
              <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                📱 {r.contact}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Plan", key: "plan", width: 140,
      render: (_, r) => {
        if (r.plan_name) {
          return (
            <Tag color="pink" style={{ fontWeight: 600 }}>
              💎 {r.plan_name}
            </Tag>
          );
        }
        if (r._rowType === 'withdrawal') {
          return <Tag color="blue">💳 Withdrawal</Tag>;
        }
        return <span style={{ color: '#94a3b8' }}>-</span>;
      },
    },
    // ✅ PLAN AMOUNT COLUMN (User ne kitna paid)
    {
      title: (
        <div>
          <ShoppingOutlined style={{ marginRight: 4 }} />
          Plan Amount
        </div>
      ),
      key: "plan_amount",
      width: 120,
      render: (_, r) => {
        if (r.plan_amount > 0) {
          return (
            <div>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#3b82f6' }}>
                ₹{r.plan_amount.toLocaleString("en-IN")}
              </span>
              <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>
                User Paid
              </div>
            </div>
          );
        }
        return <span style={{ color: '#94a3b8' }}>-</span>;
      },
    },
    // ✅ COMMISSION COLUMN (Staff ko kitna mila)
    {
      title: (
        <div style={{ color: '#10b981' }}>
          <TrophyOutlined style={{ marginRight: 4 }} />
          My Commission
        </div>
      ),
      key: "commission",
      width: 130,
      render: (_, r) => {
        if (r.commission_amount > 0) {
          return (
            <div>
              <span style={{ 
                fontWeight: 800, 
                fontSize: 15, 
                color: r.type === 'credit' ? '#10b981' : '#ef4444' 
              }}>
                {r.type === 'credit' ? '+' : '-'}₹{r.commission_amount.toLocaleString("en-IN")}
              </span>
              {r.plan_amount > 0 && (
                <div style={{ fontSize: 9, color: '#10b981', marginTop: 2, fontWeight: 600 }}>
                  {((r.commission_amount / r.plan_amount) * 100).toFixed(1)}% commission
                </div>
              )}
            </div>
          );
        }
        return <span style={{ color: '#94a3b8' }}>-</span>;
      },
    },
    {
      title: "Status", dataIndex: "status", key: "status", width: 100,
      render: (s) => {
        const isSuccess = s === "success" || s === "approved" || s === "completed";
        const isPending = s === "pending";
        return (
          <Tag className={`stx-status-tag stx-status-${isSuccess ? "success" : isPending ? "pending" : "failed"}`}>
            {isSuccess ? <CheckCircleOutlined /> : isPending ? <ClockCircleOutlined /> : <CloseCircleOutlined />}
            {" "}{capitalize(s) || "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "Date", key: "date", width: 110,
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 12 }}>{formatDate(r.date)}</div>
          <div style={{ fontSize: 10, color: "#888" }}>
            {r.date ? new Date(r.date).toLocaleTimeString("en-US", {
              hour: "2-digit", minute: "2-digit",
            }) : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Action", key: "action", width: 60, fixed: "right",
      render: (_, r) => (
        <Tooltip title="View Details">
          <Button size="small" icon={<EyeOutlined />} className="stx-action-btn"
            onClick={() => { setSelectedTx(r); setDrawerOpen(true); }} />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="stx-page">

      {/* ===== HEADER ===== */}
      <div className="stx-header">
        <div className="stx-header-left">
          <div className="stx-title-row">
            <div className="stx-title-icon"><TransactionOutlined /></div>
            <div className="stx-title-content">
              <h2 className="stx-title">Staff Transaction History</h2>
              <p className="stx-subtitle">
                {filtered.length} transactions
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  👥 {referredUsersCount} referred users
                </Tag>
              </p>
            </div>
          </div>
        </div>
        <div className="stx-header-actions">
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading} className="stx-icon-btn" />
          </Tooltip>
          <Dropdown menu={csvMenu}>
            <Button icon={<FileExcelOutlined />} className="stx-csv-btn">
              <Space size={4}>CSV<DownOutlined style={{ fontSize: 10 }} /></Space>
            </Button>
          </Dropdown>
          <Dropdown menu={pdfMenu}>
            <Button icon={<FilePdfOutlined />} className="stx-pdf-btn">
              <Space size={4}>PDF<DownOutlined style={{ fontSize: 10 }} /></Space>
            </Button>
          </Dropdown>
        </div>
      </div>

      {/* ===== MINI STATS - 2 SECTIONS ===== */}
      <div className="stx-mini-stats">
        <div className="stx-mini-stat">
          <TransactionOutlined className="stx-mini-icon total" />
          <b>{stats.total}</b> <span>Total Txns</span>
        </div>
        <div className="stx-mini-divider" />
        
        {/* ✅ NEW - Plan Purchase Total (blue) */}
        <div className="stx-mini-stat">
          <ShoppingOutlined style={{ color: '#3b82f6', fontSize: 14 }} />
          <b style={{ color: '#3b82f6' }}>₹{stats.planPurchase.toLocaleString("en-IN")}</b>
          <span>Plan Purchase</span>
        </div>
        <div className="stx-mini-divider" />
        
        {/* ✅ NEW - My Commission Total (green) */}
        <div className="stx-mini-stat">
          <TrophyOutlined style={{ color: '#10b981', fontSize: 14 }} />
          <b style={{ color: '#10b981' }}>₹{stats.commission.toLocaleString("en-IN")}</b>
          <span>My Commission</span>
        </div>
        <div className="stx-mini-divider" />
        
        <div className="stx-mini-stat">
          <FallOutlined className="stx-mini-icon debit" />
          <b>₹{stats.debit.toLocaleString("en-IN")}</b> <span>Debit</span>
        </div>
        <div className="stx-mini-divider" />
        
        <div className="stx-mini-stat">
          <CheckCircleOutlined className="stx-mini-icon success" />
          <b>{stats.success}</b> <span>Success</span>
        </div>
        <div className="stx-mini-divider" />
        
        <div className="stx-mini-stat">
          <ClockCircleOutlined className="stx-mini-icon pending" />
          <b>{stats.pending}</b> <span>Pending</span>
        </div>
        <div className="stx-mini-divider" />
        
        <div className="stx-mini-stat">
          <CloseCircleOutlined className="stx-mini-icon failed" />
          <b>{stats.failed}</b> <span>Failed</span>
        </div>
      </div>

      {/* ===== FILTERS ===== */}
      <div className="stx-filters">
        <Input
          placeholder="Search user, ref, plan, amount, UTI..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="stx-search"
          allowClear
        />
        <Select value={statusFilter} onChange={setStatusFilter} className="stx-select">
          <Option value="all">All Status</Option>
          <Option value="success">✅ Success</Option>
          <Option value="pending">⏳ Pending</Option>
          <Option value="failed">❌ Failed</Option>
        </Select>
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          size="middle"
          className="stx-datepicker"
        />
        <Button className="stx-clear-btn" onClick={clearFilters}>
          Clear
        </Button>
      </div>

           {/* ===== TABLE (Desktop Only) ===== */}
      <div className="stx-table-card stx-desktop-only">
        <Table
          rowKey={(record, index) => record._uniqueId || `row-${index}`}
          columns={columns}
          dataSource={filtered}
          loading={loading}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            size: "small",
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`,
          }}
          scroll={{ x: 1100 }}
          className="stx-table"
          locale={{ emptyText: "No transactions found for your referred users" }}
        />
      </div>

      {/* ===== MOBILE CARDS ===== */}
      <div className="stx-mobile-cards stx-mobile-only">
        {filtered.length > 0 ? (
          filtered.map((tx) => {
            const isSuccess = tx.status === "success" || tx.status === "approved" || tx.status === "completed";
            const isPending = tx.status === "pending";
            const refId = generateRefNumber("transaction", tx.id);

            return (
              <div key={tx._uniqueId || tx.id} className="stx-mcard">
                {/* Top: Ref + Status */}
                <div className="stx-mcard-top">
                  <span className="stx-mcard-ref">{refId}</span>
                  <span className={`stx-mcard-status ${isSuccess ? 'success' : isPending ? 'pending' : 'failed'}`}>
                    {isSuccess ? <CheckCircleOutlined /> : isPending ? <ClockCircleOutlined /> : <CloseCircleOutlined />}
                    {capitalize(tx.status) || 'N/A'}
                  </span>
                </div>

                {/* Body: User + Plan */}
                <div className="stx-mcard-body">
                  <div className="stx-mcard-user">
                    <div className="stx-mcard-avatar">
                      {(tx.user_name?.[0] || 'U').toUpperCase()}
                    </div>
                    <div className="stx-mcard-user-info">
                      <h4 className="stx-mcard-name">{tx.user_name}</h4>
                      {tx.contact && (
                        <span className="stx-mcard-contact"><PhoneOutlined /> {tx.contact}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="stx-mcard-plan-wrap">
                    {tx.plan_name ? (
                      <span className="stx-mcard-plan pink">💎 {tx.plan_name}</span>
                    ) : tx._rowType === 'withdrawal' ? (
                      <span className="stx-mcard-plan blue">💳 Withdrawal</span>
                    ) : null}
                  </div>
                </div>

                {/* Amounts Grid */}
                <div className="stx-mcard-amounts">
                  {tx.plan_amount > 0 && (
                    <div className="stx-mcard-amount-box plan">
                      <span className="label">User Paid</span>
                      <strong className="value">₹{tx.plan_amount.toLocaleString("en-IN")}</strong>
                    </div>
                  )}
                  {tx.commission_amount > 0 && (
                    <div className="stx-mcard-amount-box commission">
                      <span className="label">Commission</span>
                      <strong className={`value ${tx.type === 'credit' ? 'credit' : 'debit'}`}>
                        {tx.type === 'credit' ? '+' : '-'}₹{tx.commission_amount.toLocaleString("en-IN")}
                      </strong>
                    </div>
                  )}
                </div>

                {/* Footer: Date + View */}
                <div className="stx-mcard-footer">
                  <span className="stx-mcard-date">
                    <CalendarOutlined /> {formatDate(tx.date)}
                  </span>
                  <button 
                    className="stx-mcard-btn-view"
                    onClick={() => { setSelectedTx(tx); setDrawerOpen(true); }}
                  >
                    <EyeOutlined /> View Details
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="stx-empty">
            <TransactionOutlined className="stx-empty-icon" />
            <h3>No Transactions Found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* ===== DETAILS DRAWER ===== */}
      <Drawer
  title={null}
  placement="right"
  width={520}
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  closable={false}
  className="stx-premium-drawer"
  bodyStyle={{ padding: 0, background: '#f8fafc' }}
>
  {selectedTx && (
    <div className="stx-premium-details">

      {/* ===== TOP HERO SECTION ===== */}
      <div className="stx-hero-section">
        <div className="stx-hero-pattern"></div>
        
        <div className="stx-hero-top">
          <div className="stx-hero-badge-wrap">
            <TransactionOutlined className="stx-hero-icon" />
            <span>Transaction Details</span>
          </div>
          <button className="stx-hero-close" onClick={() => setDrawerOpen(false)}>
            <CloseCircleOutlined />
          </button>
        </div>

        <div className="stx-hero-ref">
          {generateRefNumber("transaction", selectedTx.id)}
        </div>

        <div className="stx-hero-status">
          <span className={`stx-hero-status-badge stx-hs-${
            selectedTx.status === "success" || selectedTx.status === "completed" || selectedTx.status === "approved"
              ? "success" : selectedTx.status === "pending" ? "pending" : "failed"
          }`}>
            {selectedTx.status === "success" || selectedTx.status === "completed" || selectedTx.status === "approved"
              ? <CheckCircleOutlined /> : selectedTx.status === "pending" 
              ? <ClockCircleOutlined /> : <CloseCircleOutlined />}
            {" "}{capitalize(selectedTx.status) || "N/A"}
          </span>
        </div>
      </div>

      {/* ===== AMOUNTS CARDS ===== */}
      <div className="stx-amounts-grid">
        {selectedTx.plan_amount > 0 && (
          <div className="stx-amount-card stx-amount-plan">
            <div className="stx-amount-card-icon">
              <ShoppingOutlined />
            </div>
            <div className="stx-amount-card-label">Plan Amount</div>
            <div className="stx-amount-card-value">
              ₹{selectedTx.plan_amount.toLocaleString("en-IN")}
            </div>
            <div className="stx-amount-card-sub">User Paid</div>
          </div>
        )}
        
        {selectedTx.commission_amount > 0 && (
          <div className="stx-amount-card stx-amount-commission">
            <div className="stx-amount-card-icon">
              <TrophyOutlined />
            </div>
            <div className="stx-amount-card-label">Your Commission</div>
            <div className="stx-amount-card-value">
              {selectedTx.type === 'credit' ? '+' : '-'}₹{selectedTx.commission_amount.toLocaleString("en-IN")}
            </div>
            {selectedTx.plan_amount > 0 && (
              <div className="stx-amount-card-sub">
                🎯 {((selectedTx.commission_amount / selectedTx.plan_amount) * 100).toFixed(1)}% Commission
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== INFO SECTIONS ===== */}
      <div className="stx-info-wrapper">

        {/* === User Info === */}
        <div className="stx-info-card">
          <div className="stx-info-card-header">
            <div className="stx-info-card-icon" style={{ background: 'linear-gradient(135deg, #6366f1, #818cf8)' }}>
              <UserOutlined />
            </div>
            <div className="stx-info-card-title">User Information</div>
          </div>
          <div className="stx-info-card-body">
            <div className="stx-info-row">
              <span className="stx-info-label">Name</span>
              <span className="stx-info-value stx-info-strong">{selectedTx.user_name}</span>
            </div>
            {selectedTx.contact && (
              <div className="stx-info-row">
                <span className="stx-info-label">📱 Contact</span>
                <span className="stx-info-value">{selectedTx.contact}</span>
              </div>
            )}
          </div>
        </div>

        {/* === Plan Info === */}
        {selectedTx.plan_name && (
          <div className="stx-info-card">
            <div className="stx-info-card-header">
              <div className="stx-info-card-icon" style={{ background: 'linear-gradient(135deg, #ec4899, #f472b6)' }}>
                <GiftOutlined />
              </div>
              <div className="stx-info-card-title">Plan Details</div>
            </div>
            <div className="stx-info-card-body">
              <div className="stx-info-row">
                <span className="stx-info-label">Plan Name</span>
                <span className="stx-info-value">
                  <Tag color="pink" style={{ fontWeight: 700, fontSize: 12, padding: '2px 10px' }}>
                    💎 {selectedTx.plan_name}
                  </Tag>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* === Transaction Info === */}
        <div className="stx-info-card">
          <div className="stx-info-card-header">
            <div className="stx-info-card-icon" style={{ background: 'linear-gradient(135deg, #10b981, #34d399)' }}>
              <FileTextOutlined />
            </div>
            <div className="stx-info-card-title">Transaction Info</div>
          </div>
          <div className="stx-info-card-body">
            <div className="stx-info-row">
              <span className="stx-info-label">📅 Date</span>
              <span className="stx-info-value">{formatDateTime(selectedTx.date)}</span>
            </div>
            <div className="stx-info-row">
              <span className="stx-info-label">Type</span>
              <span className="stx-info-value">
                <Tag color={selectedTx.type === 'credit' ? 'green' : 'red'} style={{ fontWeight: 600 }}>
                  {selectedTx.type === 'credit' ? '⬆ CREDIT' : '⬇ DEBIT'}
                </Tag>
              </span>
            </div>
            {selectedTx.UTI && (
              <div className="stx-info-row">
                <span className="stx-info-label">UTI</span>
                <span className="stx-info-value stx-info-mono">{selectedTx.UTI}</span>
              </div>
            )}
          </div>
        </div>

        {/* === Payment Info === */}
        {selectedTx.upi_id && (
          <div className="stx-info-card">
            <div className="stx-info-card-header">
              <div className="stx-info-card-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }}>
                <CreditCardOutlined />
              </div>
              <div className="stx-info-card-title">Payment Method</div>
            </div>
            <div className="stx-info-card-body">
              <div className="stx-info-row">
                <span className="stx-info-label">💳 UPI ID</span>
                <span className="stx-info-value stx-info-mono">{selectedTx.upi_id}</span>
              </div>
            </div>
          </div>
        )}

        {/* === Bottom Note === */}
        <div className="stx-bottom-note">
          <div className="stx-note-icon">🔒</div>
          <div className="stx-note-text">
            <strong>Secure Transaction</strong>
            <p>All transaction details are encrypted and safe with us.</p>
          </div>
        </div>

      </div>
    </div>
  )}
</Drawer>
    </div>
  );
}

export default StaffTransactions;